// eslint-disable-next-line no-restricted-imports
import {
  COMMENT_MODERATION_APPROVED,
  COMMENT_MODERATION_ESCALATED,
  EVENT_APPROVE_COMMENT,
  EVENT_BLOCK_COMMENT,
  EVENT_DELETE_COMMENT,
  EVENT_DOWNVOTE_COMMENT,
  EVENT_ESCALATE_COMMENT,
  EVENT_LIKE_COMMENT,
  EVENT_MODERATOR_DELETE_COMMENT,
  EVENT_REMOVE_VOTE_FROM_COMMENT,
  EVENT_UNLIKE_COMMENT,
  EVENT_UPVOTE_COMMENT,
  KIND_LIKE,
  MODERATION_APPROVE,
  MODERATION_BLOCK,
  MODERATION_ESCALATE,
} from "../constants/story-engagement-constants.mjs";
import { StoryEngagementError, VOTE_DOWN, VOTE_NONE, VOTE_UP, isLiked } from "./story-engagement.mjs"; // eslint-disable-line no-restricted-imports

export { VOTE_DOWN, VOTE_NONE, VOTE_UP };

const RICH_TEXT = "rich_text";
const MENTION_STYLE = "AT_SYMBOL";
const TIME_STEPS = [
  [7 * 86400, "w"],
  [86400, "d"],
  [3600, "h"],
  [60, "m"],
];
const HTTP_CONFLICT = 409;
const INVISIBLE_OR_SPACE = /[\s\p{Cf}\p{Z}]+/gu;
export const MAX_COMMENT_CHARACTERS = 5000;
export const REASON_COMMENT_EMPTY = "comment_empty";
export const REASON_COMMENT_TOO_LONG = "comment_too_long";
export const REASON_COMMENT_MEDIA_NOT_ALLOWED = "comment_media_not_allowed";
const MAX_MENTION_SUGGESTIONS = 8;
const STOCK_AVATAR = /\/avatar-default\.png(\?|$)/;
export const BUBBLE_SHOWS_COMMENTS = "show";
export const BUBBLE_OPENS_OVERLAY = "overlay";
export const OVERLAY_SHEET = "sheet";
export const OVERLAY_SPLIT = "split";
export const OVERLAY_SINGLE = "single";
export const QUICK_REACTION_EMOJI = Object.freeze(["❤️", "🙌", "🔥", "👏", "😢", "😍", "😮", "😂"]);
const SHEET_MAX_VIEWPORT_WIDTH = 767;
const COMMENT_VOTE_EVENTS = { [VOTE_UP]: EVENT_UPVOTE_COMMENT, [VOTE_DOWN]: EVENT_DOWNVOTE_COMMENT };
const MSG_NO_COMMENT_VOTE_EVENT = "A comment vote has no StoryEngagement event; expected 1, 0 or -1";
const COMMENT_MODERATION_EVENTS = {
  [MODERATION_APPROVE]: EVENT_APPROVE_COMMENT,
  [MODERATION_BLOCK]: EVENT_BLOCK_COMMENT,
  [MODERATION_ESCALATE]: EVENT_ESCALATE_COMMENT,
};
const MSG_NO_COMMENT_MODERATION_EVENT = "A comment moderation has no StoryEngagement event; expected approve, block or escalate";
const MSG_COMMENT_ROW_WITHOUT_REAL_ID = "A StoryComment row has no real id yet";

export class SentCommentNotEchoedError extends StoryEngagementError {
  constructor(message, data = {}) {
    super(message, data);
    this.name = "SentCommentNotEchoedError";
  }
}

function textSegments(body) {
  return [{ segment_type: RICH_TEXT, segment_content: { body_text: body, ranges: [] } }];
}

function isSegment(value) {
  return value !== null && typeof value === "object" && typeof value.segment_type === "string";
}

export function pictureOrInitials(avatarUrl) {
  return STOCK_AVATAR.test(avatarUrl) ? "" : avatarUrl;
}

export function segmentsOfMessage(message) {
  if (typeof message !== "string") {
    throw new TypeError(`A comment message must be a string of text or JSON segments, got ${JSON.stringify(message)}`);
  }
  let parsed;
  try {
    parsed = JSON.parse(message);
  } catch {
    return textSegments(message);
  }
  if (!Array.isArray(parsed) || parsed.length === 0 || !parsed.every(isSegment)) {
    return textSegments(message);
  }
  return parsed;
}

export function messageOfSegments(segments) {
  return JSON.stringify(segments);
}

export function plainTextOf(segments) {
  return segments
      .filter(segment => segment.segment_type === RICH_TEXT)
      .map(segment => segment.segment_content?.body_text ?? segment.segment_content?.data?.body_text ?? "")
      .join("\n");
}

function voteOf(raw) {
  if (isLiked(raw.user_up_voted)) {
    return VOTE_UP;
  }
  if (isLiked(raw.user_down_voted)) {
    return VOTE_DOWN;
  }
  return VOTE_NONE;
}

export function commentFromApi(raw) {
  const id = Number(raw?.message_id);
  if (!Number.isFinite(id) || id < 1) {
    throw new TypeError(`A comment from the API has no message_id: ${JSON.stringify(raw).slice(0, 200)}`);
  }
  return {
    id,
    parentId: Number(raw.parent_id ?? 0),
    authorId: Number(raw.user_id ?? 0),
    authorName: String(raw.author_name ?? ""),
    avatarUrl: pictureOrInitials(String(raw.avatar_url ?? "")),
    segments: segmentsOfMessage(raw.message ?? ""),
    likeCount: Number(raw.num_likes ?? 0),
    liked: isLiked(raw.user_liked),
    reported: isLiked(raw.user_reported),
    score: Number(raw.vote_score ?? 0),
    vote: voteOf(raw),
    replyCount: Number(raw.num_approved_replies ?? 0),
    pending: raw.moderation_status !== COMMENT_MODERATION_APPROVED,
    escalated: raw.moderation_status === COMMENT_MODERATION_ESCALATED,
    edited: Number(raw.timestamp_edited ?? 0) > 0,
    createdAt: Number(raw.timestamp_created ?? 0),
    replies: (raw.replies ?? []).map(commentFromApi),
  };
}

function snakeCase(key) {
  return key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function snakeCaseKeys(value) {
  if (Array.isArray(value)) {
    return value.map(snakeCaseKeys);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [snakeCase(key), snakeCaseKeys(entry)]));
}

export function reportWasAlreadySent(error) {
  return Number(error?.response?.status) === HTTP_CONFLICT;
}

export function commentFromSentMessage(raw) {
  return commentFromApi(snakeCaseKeys(raw));
}

export function replyPrefillSegments({ id, label }) {
  const handle = `@${label.replace(/\s+/g, "")}`;
  return [{
    segment_type: RICH_TEXT,
    segment_content: {
      body_text: `${handle} `,
      ranges: [{ style: MENTION_STYLE, start: 0, end: handle.length, entityId: id, title: label }],
    },
  }];
}

export function mentionCandidates(comments) {
  const seen = new Map();
  const visit = comment => {
    if (comment.authorName !== "" && !seen.has(comment.authorId)) {
      seen.set(comment.authorId, { id: comment.authorId, label: comment.authorName });
    }
    comment.replies.forEach(visit);
  };
  comments.forEach(visit);
  return [...seen.values()];
}

export function mentionCompletionFor(getComments) {
  return {
    trigger: "@",
    insert: "mention",
    source: query => mentionCandidates(getComments())
        .filter(person => person.label.toLowerCase().startsWith(query.toLowerCase()))
        .slice(0, MAX_MENTION_SUGGESTIONS),
  };
}

export function commentProblemOf(segments) {
  if (segments.some(segment => segment.segment_type !== RICH_TEXT)) {
    return REASON_COMMENT_MEDIA_NOT_ALLOWED;
  }
  const text = plainTextOf(segments);
  if (text.replace(INVISIBLE_OR_SPACE, "") === "") {
    return REASON_COMMENT_EMPTY;
  }
  if ([...text].length > MAX_COMMENT_CHARACTERS) {
    return REASON_COMMENT_TOO_LONG;
  }
  return null;
}

export function nextPageOf(metadata) {
  const cursor = metadata?.lastMessageId === undefined
    ? null
    : { last_sort_id: metadata.lastSortId, last_message_id: metadata.lastMessageId };
  return { cursor, hasMore: Boolean(metadata?.hasMore) && cursor !== null };
}

export function inReplyOrder(replies) {
  return [...replies].sort((a, b) => a.createdAt - b.createdAt || a.id - b.id);
}

export function repliesCursorAfter(replies) {
  const last = replies[replies.length - 1];
  return last ? { last_sort_id: last.createdAt, last_message_id: last.id } : null;
}

export function mergeComments(shown, incoming) {
  const byId = new Map(shown.map(comment => [comment.id, comment]));
  incoming.forEach(comment => byId.set(comment.id, comment));
  return [...byId.values()];
}

export function refreshedComments(shown, freshPage) {
  const shownById = new Map(shown.map(comment => [comment.id, comment]));
  const freshIds = new Set(freshPage.map(comment => comment.id));
  const ownWaiting = shown.filter(comment => comment.pending && !freshIds.has(comment.id));
  const refreshed = freshPage.map(fresh => {
    const before = shownById.get(fresh.id);
    return before ? { ...fresh, replies: inReplyOrder(mergeComments(before.replies, fresh.replies)) } : fresh;
  });
  return [...ownWaiting, ...refreshed];
}

export function likedComment(comment, liked) {
  if (comment.liked === liked) {
    return comment;
  }
  return { ...comment, liked, likeCount: comment.likeCount + (liked ? 1 : -1) };
}

export function votedComment(comment, vote) {
  return { ...comment, vote, score: comment.score - comment.vote + vote };
}

export function commentsAreFolded({ inFeedRow, editMode }) {
  return inFeedRow && !editMode;
}

export function bubbleTapOutcome({ inFeedRow }) {
  return inFeedRow ? BUBBLE_OPENS_OVERLAY : BUBBLE_SHOWS_COMMENTS;
}

export function overlayLayoutFor({ viewportWidth, hasCover }) {
  if (!Number.isFinite(viewportWidth)) {
    throw new Error(`The comments overlay needs the viewport width to pick its layout, got ${JSON.stringify(viewportWidth)}`);
  }
  if (viewportWidth <= SHEET_MAX_VIEWPORT_WIDTH) {
    return OVERLAY_SHEET;
  }
  return hasCover ? OVERLAY_SPLIT : OVERLAY_SINGLE;
}

export function timeAgo(createdAtSeconds, nowSeconds) {
  const elapsed = nowSeconds - createdAtSeconds;
  for (const [size, unit] of TIME_STEPS) {
    if (elapsed >= size) {
      return `${Math.floor(elapsed / size)}${unit}`;
    }
  }
  return "just now";
}

const VOTE_REQUESTS = { [VOTE_UP]: "upvote", [VOTE_DOWN]: "downvote" };
const CLEAR_VOTE_REQUESTS = { [VOTE_UP]: "revoke-upvote", [VOTE_DOWN]: "revoke-downvote" };

export function engagementFieldsOfComment(comment) {
  return {
    likeStatus: comment.liked,
    likeCount: comment.likeCount,
    voteStatus: comment.vote,
    voteScore: comment.score,
    upvoteCount: 0,
    downvoteCount: 0,
  };
}

export function commentWithEngagement(comment, fields) {
  return {
    ...comment,
    liked: isLiked(fields.likeStatus),
    likeCount: Number(fields.likeCount),
    vote: Number(fields.voteStatus),
    score: Number(fields.voteScore),
  };
}

export function commentEngagementAction(kind, target, confirmedVote) {
  if (kind === KIND_LIKE) {
    return target ? "like" : "revoke-like";
  }
  if (target === VOTE_NONE) {
    return CLEAR_VOTE_REQUESTS[confirmedVote] ?? null;
  }
  return VOTE_REQUESTS[target];
}


const commentRef = commentId => ({ id: Number(commentId) });

export function buildCommentEngagementEvent(kind, commentId, target, confirmedVote) {
  if (kind === KIND_LIKE) {
    return { eventType: target ? EVENT_LIKE_COMMENT : EVENT_UNLIKE_COMMENT, params: { comment: commentRef(commentId) } };
  }
  if (target === VOTE_NONE) {
    return confirmedVote === VOTE_NONE ? null : { eventType: EVENT_REMOVE_VOTE_FROM_COMMENT, params: { comment: commentRef(commentId) } };
  }
  if (!Object.hasOwn(COMMENT_VOTE_EVENTS, target)) {
    throw new StoryEngagementError(MSG_NO_COMMENT_VOTE_EVENT, { commentId, target });
  }
  return { eventType: COMMENT_VOTE_EVENTS[target], params: { comment: commentRef(commentId) } };
}

export function postCommentParams(storyId, segments) {
  return { story: Number(storyId), body: segments };
}

export function replyParams(storyId, parentId, segments) {
  return { story: Number(storyId), parent: commentRef(parentId), body: segments };
}

export function editParams(commentId, segments) {
  return { comment: commentRef(commentId), body: segments };
}

export function reportParams(commentId, reason) {
  return { comment: commentRef(commentId), reason };
}

export function buildBlockPersonParams(userId) {
  return { blockedUser: { id: userId } };
}

function commentOnStoryParams(storyId, comment) {
  const params = { story: Number(storyId), comment: commentRef(comment.id) };
  if (comment.parentId > 0) {
    params.parent = commentRef(comment.parentId);
  }
  return params;
}

export function buildCommentRemovalEvent(storyId, comment, readerId) {
  const eventType = comment.authorId === readerId ? EVENT_DELETE_COMMENT : EVENT_MODERATOR_DELETE_COMMENT;
  return { eventType, params: commentOnStoryParams(storyId, comment) };
}

export function buildCommentModerationEvent(storyId, comment, action) {
  if (!Object.hasOwn(COMMENT_MODERATION_EVENTS, action)) {
    throw new StoryEngagementError(MSG_NO_COMMENT_MODERATION_EVENT, { commentId: comment.id, action });
  }
  return { eventType: COMMENT_MODERATION_EVENTS[action], params: commentOnStoryParams(storyId, comment) };
}

export function commentFromStoryEngagementRow(row, reader) {
  if (!Number.isInteger(row.id) || row.id < 1) {
    throw new StoryEngagementError(MSG_COMMENT_ROW_WITHOUT_REAL_ID, { id: row.id });
  }
  return {
    id: row.id,
    parentId: row.parentComment ? Number(row.parentComment.pk.id) : 0,
    authorId: reader.id,
    authorName: reader.name,
    avatarUrl: pictureOrInitials(reader.avatarUrl),
    segments: Array.isArray(row.body) ? row.body : segmentsOfMessage(row.body),
    likeCount: row.likeCount,
    liked: false,
    reported: false,
    score: row.upvoteCount - row.downvoteCount,
    vote: VOTE_NONE,
    replyCount: row.replyCount,
    pending: row.moderationStatus !== COMMENT_MODERATION_APPROVED,
    escalated: row.moderationStatus === COMMENT_MODERATION_ESCALATED,
    edited: row.timestampEdited > 0,
    createdAt: row.timestampCreated,
    replies: [],
  };
}
